import React, { useEffect, useRef, useState } from "react";

export interface SwitcherOption {
  id: number;
  label: string;
  detail?: string;
}

interface EntitySwitcherProps {
  ariaLabel: string;
  placeholder: string;
  search: (term: string) => Promise<SwitcherOption[]>;
  onSelect: (option: SwitcherOption) => void;
  currentId?: number;
  // Content of the button that opens the list; defaults to "<current> ▾".
  current?: string;
  trigger?: React.ReactNode;
  triggerClassName?: string;
  align?: "start" | "end";
}

// Searchable dropdown used to jump straight to another company or API key
// without going back to a list first.
const EntitySwitcher: React.FC<EntitySwitcherProps> = ({
  ariaLabel,
  placeholder,
  search,
  onSelect,
  currentId,
  current,
  trigger,
  triggerClassName = "btn btn-link p-0 align-baseline text-decoration-none",
  align = "start",
}) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [options, setOptions] = useState<SwitcherOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const requestId = useRef(0);

  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    const id = ++requestId.current;
    setLoading(true);
    const timer = setTimeout(() => {
      searchRef.current(term.trim())
        .then((results) => {
          if (id !== requestId.current) return;
          setOptions(results);
          setHighlighted(0);
        })
        .catch(() => {
          if (id === requestId.current) setOptions([]);
        })
        .finally(() => {
          if (id === requestId.current) setLoading(false);
        });
    }, term ? 250 : 0);
    return () => clearTimeout(timer);
  }, [open, term]);

  const close = () => {
    setOpen(false);
    setTerm("");
  };

  const select = (option: SwitcherOption) => {
    close();
    onSelect(option);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (options[highlighted]) select(options[highlighted]);
    } else if (e.key === "Escape") {
      close();
    }
  };

  return (
    <div className="position-relative d-inline-block" ref={ref}>
      <button
        type="button"
        className={triggerClassName}
        onClick={() => (open ? close() : setOpen(true))}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={ariaLabel}
      >
        {trigger ?? (
          <>
            {current}
            <i className="bi bi-chevron-down ms-1 small"></i>
          </>
        )}
      </button>

      {open && (
        <div
          className="dropdown-menu show p-2 shadow"
          style={{
            top: "100%",
            [align === "end" ? "right" : "left"]: 0,
            marginTop: 4,
            minWidth: 320,
            maxWidth: "90vw",
            zIndex: 1030,
          }}
        >
          <input
            type="text"
            className="form-control form-control-sm mb-2"
            placeholder={placeholder}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          {loading && options.length === 0 ? (
            <div className="d-flex justify-content-center py-2">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : options.length === 0 ? (
            <div className="text-muted small px-2 py-1">No matches</div>
          ) : (
            <ul className="list-unstyled mb-0" role="listbox" style={{ maxHeight: 320, overflowY: "auto" }}>
              {options.map((option, i) => (
                <li key={option.id} role="option" aria-selected={i === highlighted}>
                  <button
                    type="button"
                    className={`dropdown-item rounded-1 ${i === highlighted ? "active" : ""}`}
                    onMouseEnter={() => setHighlighted(i)}
                    onClick={() => select(option)}
                  >
                    <div className="d-flex justify-content-between align-items-center gap-2">
                      <span className="text-truncate">{option.label}</span>
                      {option.id === currentId && <i className="bi bi-check2"></i>}
                    </div>
                    {option.detail && (
                      <small className={`d-block text-truncate ${i === highlighted ? "" : "text-muted"}`}>
                        {option.detail}
                      </small>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default EntitySwitcher;
